import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Row, Col, Typography, Flex, Select, Button, Table, Tag, Progress, Spin, Empty, Badge, DatePicker, Input, Tabs, Popconfirm, message, Avatar } from "antd";
import { ArrowLeftOutlined, SearchOutlined, CalendarOutlined, PhoneOutlined, ContactsOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getEmployeeCallStats, getEmployeeCallDetails, resetEmployee2FA } from "../../services/employeeService";
import { getEmployeeLeads } from "../../services/leadService";
import LeadsTemplate from "../../general/components/leads-template/LeadsTemplate";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Duration format helper
function formatDuration(totalSeconds) {
  if (!totalSeconds || totalSeconds <= 0) return "00:00:00";
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function EmployeeDashboardView({ employee, companyCode, onBack, onSyncEmployee }) {
  const [activeTabKey, setActiveTabKey] = useState("overview");

  // Overview / Telephony Filter States
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [customRange, setCustomRange] = useState(null);
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetting2FA, setResetting2FA] = useState(false);
  const [is2FAActive, setIs2FAActive] = useState(Boolean(employee?.twoFactorEnabled));
  const [stats, setStats] = useState({});
  const [calls, setCalls] = useState([]);
  const [assignedLeadCount, setAssignedLeadCount] = useState(0);

  const empId = employee?._id || employee?.id;

  useEffect(() => {
    setIs2FAActive(Boolean(employee?.twoFactorEnabled));
  }, [employee?.twoFactorEnabled]);

  const handleReset2FA = async () => {
    if (!empId || !companyCode) return;
    setResetting2FA(true);
    try {
      const res = await resetEmployee2FA(empId, companyCode);
      if (res?.success) {
        setIs2FAActive(false);
        message.success("2FA reset successfully. A new QR code will be generated on next login.");
      } else {
        message.error(res?.message || "Failed to reset 2FA.");
      }
    } catch (err) {
      console.error("Failed to reset 2FA:", err);
      message.error("Failed to reset 2FA.");
    } finally {
      setResetting2FA(false);
    }
  };

  // Resolve start/end dates based on period selection
  const { fromStr, toStr } = useMemo(() => {
    const today = dayjs();
    if (selectedPeriod === "today") {
      const d = today.format("YYYY-MM-DD");
      return { fromStr: d, toStr: d };
    }
    if (selectedPeriod === "yesterday") {
      const y = today.subtract(1, "day").format("YYYY-MM-DD");
      return { fromStr: y, toStr: y };
    }
    if (selectedPeriod === "lastweek") {
      return {
        fromStr: today.subtract(6, "day").format("YYYY-MM-DD"),
        toStr: today.format("YYYY-MM-DD"),
      };
    }
    if (selectedPeriod === "month") {
      return {
        fromStr: today.startOf("month").format("YYYY-MM-DD"),
        toStr: today.format("YYYY-MM-DD"),
      };
    }
    if (selectedPeriod === "lastmonth") {
      return {
        fromStr: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        toStr: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    }
    if (selectedPeriod === "custom" && customRange?.[0] && customRange?.[1]) {
      return {
        fromStr: customRange[0].format("YYYY-MM-DD"),
        toStr: customRange[1].format("YYYY-MM-DD"),
      };
    }
    return { fromStr: today.format("YYYY-MM-DD"), toStr: today.format("YYYY-MM-DD") };
  }, [selectedPeriod, customRange]);

  // Fetch Live Employee Telephony Stats, Call History & Lead Count
  const loadEmployeeData = useCallback(async () => {
    if (!companyCode || !empId) return;
    setLoading(true);
    try {
      const [statsRes, callsRes, leadsRes] = await Promise.all([getEmployeeCallStats(companyCode, empId, selectedPeriod, fromStr, toStr), getEmployeeCallDetails(companyCode, empId, selectedPeriod, fromStr, toStr, searchQuery), getEmployeeLeads(companyCode, empId)]);

      if (statsRes?.success && statsRes.stats) {
        setStats(statsRes.stats);
      } else {
        setStats({
          total: 0,
          connected: 0,
          totalDuration: 0,
          incoming: 0,
          outgoing: 0,
          missed: 0,
          rejected: 0,
          incomingDuration: 0,
          outgoingDuration: 0,
        });
      }

      if (callsRes?.success && Array.isArray(callsRes.calls)) {
        setCalls(callsRes.calls);
      } else {
        setCalls([]);
      }

      if (leadsRes?.success) {
        const leadItems = Array.isArray(leadsRes.items || leadsRes.leads) ? leadsRes.items || leadsRes.leads : [];
        setAssignedLeadCount(leadItems.length);
      }
    } catch (e) {
      console.error("Failed to load employee telephony details", e);
    } finally {
      setLoading(false);
    }
  }, [companyCode, empId, selectedPeriod, fromStr, toStr, searchQuery]);

  useEffect(() => {
    loadEmployeeData();
  }, [loadEmployeeData]);

  // Filtered Call Records
  const filteredCalls = useMemo(() => {
    return calls.filter((c) => {
      if (callTypeFilter !== "all" && (c.callType || "").toLowerCase() !== callTypeFilter.toLowerCase()) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (c.name || "").toLowerCase().includes(q) || (c.number || "").includes(q);
      }
      return true;
    });
  }, [calls, callTypeFilter, searchQuery]);

  const callLogColumns = [
    {
      title: "Contact / Number",
      key: "contact",
      render: (_, r) => (
        <Flex vertical>
          <Text strong style={{ fontSize: "0.88rem" }}>
            {r.name || r.number || "Client"}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {r.number}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Type",
      dataIndex: "callType",
      key: "callType",
      render: (type) => {
        const t = (type || "").toLowerCase();
        let color = "default";
        if (t === "incoming") color = "blue";
        if (t === "outgoing") color = "gold";
        if (t === "missed") color = "red";
        if (t === "rejected") color = "magenta";
        return (
          <Tag color={color} style={{ borderRadius: 10, textTransform: "capitalize" }}>
            {t}
          </Tag>
        );
      },
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (dur) => <Text style={{ fontSize: "0.85rem" }}>{formatDuration(dur)}</Text>,
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (time) => <Text style={{ fontSize: "0.8rem", color: "#64748b" }}>{time ? new Date(time).toLocaleString("en-IN") : "—"}</Text>,
    },
  ];

  if (!employee) return null;

  return (
    <Spin spinning={loading} tip="Loading employee activity...">
      <Flex vertical gap={20} style={{ paddingBottom: 40 }}>
        {/* ── HEADER ── */}
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
          <Flex align="center" gap={14}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ fontSize: "1.1rem", width: 40, height: 40, borderRadius: "50%", background: "#f1f5f9" }} />
            <Avatar
              size={46}
              src={employee.profilePhoto || undefined}
              style={{
                backgroundColor: "#2459d3",
                fontWeight: 600,
                fontSize: "1.1rem",
                flexShrink: 0,
              }}
            >
              {!employee.profilePhoto && (employee.name || "E").charAt(0).toUpperCase()}
            </Avatar>
            <Flex vertical>
              <Title level={3} style={{ margin: 0, color: "#0f172a" }}>
                {employee.name}
              </Title>
              <Text type="secondary" style={{ fontSize: "0.85rem", marginTop: 4 }}>
                {employee.mobile} &bull; {employee.tags?.[0] || "Sales Rep"} &bull; #{employee.employeeCode || "EMP"}
              </Text>
            </Flex>
          </Flex>

          {/* Top Actions: Reset 2FA & Period Filter */}
          <Flex align="center" gap={12} wrap="wrap">
            <Popconfirm title="Reset 2FA Authentication" description="Are you sure you want to reset 2FA for this employee? Their existing authenticator key will be cleared and a new QR code will be generated on their next login." onConfirm={handleReset2FA} okText="Reset 2FA" cancelText="Cancel" okButtonProps={{ danger: true, loading: resetting2FA }}>
              <Button icon={<SafetyCertificateOutlined style={{ color: "#d97706" }} />} loading={resetting2FA} style={{ borderRadius: 6, fontWeight: 500 }}>
                Reset 2FA
              </Button>
            </Popconfirm>

            {activeTabKey === "overview" && (
              <Flex align="center" gap={12} wrap="wrap">
                <Flex align="center" gap={8}>
                  <CalendarOutlined style={{ color: "#2459d3" }} />
                  <Text strong style={{ fontSize: "0.85rem" }}>
                    Period
                  </Text>
                  <Select
                    value={selectedPeriod}
                    onChange={(val) => {
                      setSelectedPeriod(val);
                      if (val !== "custom") setCustomRange(null);
                    }}
                    style={{ width: 140 }}
                    options={[
                      { value: "today", label: "Today" },
                      { value: "yesterday", label: "Yesterday" },
                      { value: "lastweek", label: "Last 7 Days" },
                      { value: "month", label: "This Month" },
                      { value: "lastmonth", label: "Last Month" },
                      { value: "custom", label: "Custom Range" },
                    ]}
                  />
                </Flex>

                {selectedPeriod === "custom" && <RangePicker value={customRange} onChange={(dates) => setCustomRange(dates)} style={{ width: 230 }} allowClear={false} />}
              </Flex>
            )}
          </Flex>
        </Flex>

        {/* ── WORKSPACE TABS ── */}
        <Tabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          type="card"
          items={[
            {
              key: "overview",
              label: (
                <span>
                  <PhoneOutlined /> Performance & Telemetry
                </span>
              ),
            },
            {
              key: "leads",
              label: (
                <span>
                  <ContactsOutlined /> Assigned Leads {assignedLeadCount > 0 ? `(${assignedLeadCount})` : ""}
                </span>
              ),
            },
          ]}
        />

        {/* TAB 1: PERFORMANCE & TELEMETRY OVERVIEW */}
        {activeTabKey === "overview" && (
          <Flex vertical gap={24}>
            {/* ── KPI STRIP ── */}
            <Card bordered={false} styles={{ body: { padding: 0 } }} style={{ borderRadius: 12, background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", overflow: "hidden" }}>
              <Flex wrap="wrap" style={{ borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ flex: 1, minWidth: 140, padding: "20px 24px", borderRight: "1px solid #f1f5f9" }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                    Total Calls
                  </Text>
                  <Title level={2} style={{ margin: "4px 0 0 0", color: "#1e293b" }}>
                    {stats.total || 0}
                  </Title>
                </div>
                <div style={{ flex: 1, minWidth: 140, padding: "20px 24px", borderRight: "1px solid #f1f5f9" }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                    Connected
                  </Text>
                  <Title level={2} style={{ margin: "4px 0 0 0", color: "#10b981" }}>
                    {stats.connected || 0}
                  </Title>
                </div>
                <div style={{ flex: 1, minWidth: 160, padding: "20px 24px", borderRight: "1px solid #f1f5f9" }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                    Talk Time
                  </Text>
                  <Title level={2} style={{ margin: "4px 0 0 0", color: "#2459d3" }}>
                    {formatDuration(stats.totalDuration)}
                  </Title>
                </div>
                <div style={{ flex: 1, minWidth: 140, padding: "20px 24px", borderRight: "1px solid #f1f5f9" }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                    Incoming Talk
                  </Text>
                  <Title level={3} style={{ margin: "4px 0 0 0", color: "#0ea5e9" }}>
                    {formatDuration(stats.incomingDuration)}
                  </Title>
                </div>
                <div style={{ flex: 1, minWidth: 140, padding: "20px 24px" }}>
                  <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                    Outgoing Talk
                  </Text>
                  <Title level={3} style={{ margin: "4px 0 0 0", color: "#f59e0b" }}>
                    {formatDuration(stats.outgoingDuration)}
                  </Title>
                </div>
              </Flex>
            </Card>

            {/* ── CHARTS / BREAKDOWN ROW ── */}
            <Row gutter={[20, 20]}>
              <Col xs={24} lg={12}>
                <Card
                  bordered={false}
                  title={
                    <Flex vertical gap={2}>
                      <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                        Distribution
                      </Text>
                      <Text strong style={{ fontSize: "1.05rem" }}>
                        Call mix breakdown
                      </Text>
                    </Flex>
                  }
                  styles={{ body: { padding: 24 } }}
                  style={{ borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)", height: "100%" }}
                >
                  <Flex vertical gap={14}>
                    <Flex justify="space-between" align="center">
                      <Text>
                        <Badge color="#3b82f6" /> Incoming Calls
                      </Text>
                      <Text strong>{stats.incoming || 0}</Text>
                    </Flex>
                    <Progress percent={stats.total > 0 ? Math.round(((stats.incoming || 0) / stats.total) * 100) : 0} strokeColor="#3b82f6" size="small" />

                    <Flex justify="space-between" align="center">
                      <Text>
                        <Badge color="#f59e0b" /> Outgoing Calls
                      </Text>
                      <Text strong>{stats.outgoing || 0}</Text>
                    </Flex>
                    <Progress percent={stats.total > 0 ? Math.round(((stats.outgoing || 0) / stats.total) * 100) : 0} strokeColor="#f59e0b" size="small" />

                    <Flex justify="space-between" align="center">
                      <Text>
                        <Badge color="#ef4444" /> Missed / Unanswered
                      </Text>
                      <Text strong>{(stats.missed || 0) + (stats.rejected || 0)}</Text>
                    </Flex>
                    <Progress percent={stats.total > 0 ? Math.round((((stats.missed || 0) + (stats.rejected || 0)) / stats.total) * 100) : 0} strokeColor="#ef4444" size="small" />
                  </Flex>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  bordered={false}
                  title={
                    <Flex vertical gap={2}>
                      <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                        Telemetry
                      </Text>
                      <Text strong style={{ fontSize: "1.05rem" }}>
                        Device & Connection
                      </Text>
                    </Flex>
                  }
                  styles={{ body: { padding: 24 } }}
                  style={{ borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)", height: "100%" }}
                >
                  <Flex vertical gap={12}>
                    <Flex justify="space-between">
                      <Text type="secondary">Device Model:</Text>
                      <Text strong>{employee.deviceModel || "Android Device"}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text type="secondary">App Version:</Text>
                      <Text strong>v{employee.appVersion || "1.0.0"}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text type="secondary">Last Sync Time:</Text>
                      <Text strong>{employee.lastSyncTime ? new Date(employee.lastSyncTime).toLocaleString("en-IN") : "Never"}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text type="secondary">Active Company Code:</Text>
                      <Text strong>{companyCode}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text type="secondary">Date Scope:</Text>
                      <Tag color="blue">
                        {fromStr} to {toStr}
                      </Tag>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text type="secondary">2FA Status:</Text>
                      <Flex align="center" gap={8}>
                        <Tag color={is2FAActive ? "green" : "orange"} style={{ borderRadius: 10, margin: 0 }}>
                          {is2FAActive ? "Configured" : "Reset / Pending Setup"}
                        </Tag>
                        <Popconfirm title="Reset 2FA Authentication" description="Are you sure you want to reset 2FA? The employee will receive a new QR code upon their next web login." onConfirm={handleReset2FA} okText="Reset 2FA" cancelText="Cancel" okButtonProps={{ danger: true, loading: resetting2FA }}>
                          <Button type="link" size="small" danger loading={resetting2FA} style={{ padding: 0, height: "auto", fontSize: "0.82rem" }}>
                            Reset
                          </Button>
                        </Popconfirm>
                      </Flex>
                    </Flex>
                  </Flex>
                </Card>
              </Col>
            </Row>

            {/* ── CALL HISTORY LOGS ── */}
            <Card
              bordered={false}
              title={
                <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                  <Flex vertical gap={2}>
                    <Text type="secondary" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 700 }}>
                      Recent Telephony Records
                    </Text>
                    <Text strong style={{ fontSize: "1.05rem" }}>
                      Call history log
                    </Text>
                  </Flex>
                  <Flex align="center" gap={10} wrap="wrap">
                    <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search number or name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear style={{ width: 200 }} />
                    <Select
                      value={callTypeFilter}
                      onChange={setCallTypeFilter}
                      style={{ width: 120 }}
                      options={[
                        { value: "all", label: "All Types" },
                        { value: "incoming", label: "Incoming" },
                        { value: "outgoing", label: "Outgoing" },
                        { value: "missed", label: "Missed" },
                        { value: "rejected", label: "Rejected" },
                      ]}
                    />
                    <Text type="secondary" style={{ fontSize: "0.85rem" }}>
                      {filteredCalls.length} entries
                    </Text>
                  </Flex>
                </Flex>
              }
              styles={{ body: { padding: 0 } }}
              style={{ borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}
            >
              {filteredCalls.length > 0 ? <Table dataSource={filteredCalls} columns={callLogColumns} rowKey={(r) => r._id || r.id || `${r.number}-${r.timestamp}`} pagination={{ pageSize: 8, showSizeChanger: true }} size="middle" /> : <Empty description="No individual call records found for this period." style={{ padding: "3rem 0" }} />}
            </Card>
          </Flex>
        )}

        {/* TAB 2: ASSIGNED LEADS - USES OUR COMPLETE LEADS TEMPLATE */}
        {activeTabKey === "leads" && (
          <div style={{ marginTop: -10 }}>
            <LeadsTemplate activeTab="leads" role="admin" employeeId={empId} employee={employee} />
          </div>
        )}
      </Flex>
    </Spin>
  );
}

export default EmployeeDashboardView;
